import { boxSlotSessionKeys } from "@/modules/boxSlotSession/gateway/boxSlotSessionKey"
import { useServicesContext } from "@/modules/core/services/services.context"
import { socialKeys } from "@/modules/social/gateway/socialKeys"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

/* ── Post Reactions ── */

export const useToggleSocialReaction = ({
  socialPostId,
  sessionId,
  userId,
  exerciseId,
}: {
  socialPostId: string | null
  sessionId: string | null
  userId: string | null
  exerciseId: string | null
}) => {
  const { socialApi } = useServicesContext()
  const queryClient = useQueryClient()

  const addMutation = useMutation({
    mutationFn: async (emoji: string) => {
      if (!socialPostId) {
        if (sessionId) {
          const post = await socialApi.createSocialPost({ socialPostType: "SESSION", sessionId })
          return socialApi.addReaction(post.id, emoji)
        }
        if (userId && exerciseId) {
          const post = await socialApi.createSocialPost({ socialPostType: "EXERCISE_COMPLETION", userId, exerciseId })
          return socialApi.addReaction(post.id, emoji)
        }
        throw new Error("Session ID or Exercise ID is required to create a social post")
      }
      return socialApi.addReaction(socialPostId, emoji)
    },
    onSuccess: () => {
      if (socialPostId) {
        void queryClient.invalidateQueries({ queryKey: socialKeys.sessionPost(socialPostId) })
        void queryClient.invalidateQueries({ queryKey: socialKeys.reactions(socialPostId) })
      }
      if (exerciseId && userId) {
        void queryClient.invalidateQueries({ queryKey: socialKeys.userExercisePost(userId, exerciseId) })
      }
      void queryClient.invalidateQueries({ queryKey: ["programSession"] })
      void queryClient.invalidateQueries({ queryKey: boxSlotSessionKeys.all })
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => socialApi.removeReaction(socialPostId ?? ""),
    onSuccess: () => {
      if (socialPostId) {
        void queryClient.invalidateQueries({ queryKey: socialKeys.sessionPost(socialPostId) })
        void queryClient.invalidateQueries({ queryKey: socialKeys.reactions(socialPostId) })
      }
      if (exerciseId && userId) {
        void queryClient.invalidateQueries({ queryKey: socialKeys.userExercisePost(userId, exerciseId) })
      }
      void queryClient.invalidateQueries({ queryKey: ["programSession"] })
      void queryClient.invalidateQueries({ queryKey: boxSlotSessionKeys.all })
    },
  })

  return {
    addReaction: addMutation.mutate,
    removeReaction: removeMutation.mutate,
    isPending: addMutation.isPending || removeMutation.isPending,
  }
}

export const useSocialReactionUsers = (socialPostId: string | null) => {
  const { socialApi } = useServicesContext()

  return useQuery({
    queryKey: socialKeys.reactions(socialPostId!),
    queryFn: () => socialApi.getReactions(socialPostId!),
    enabled: !!socialPostId,
  })
}

/* ── Comment Reactions ── */

export const useToggleCommentReaction = (socialPostId: string | null, commentId: string) => {
  const { socialApi } = useServicesContext()
  const queryClient = useQueryClient()

  const addMutation = useMutation({
    mutationFn: (emoji: string) => socialApi.addCommentReaction(socialPostId!, commentId, emoji),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: socialKeys.comments(socialPostId!) })
      void queryClient.invalidateQueries({ queryKey: socialKeys.commentReactions(socialPostId!, commentId) })
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => socialApi.removeCommentReaction(socialPostId!, commentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: socialKeys.comments(socialPostId!) })
      void queryClient.invalidateQueries({ queryKey: socialKeys.commentReactions(socialPostId!, commentId) })
    },
  })

  return {
    addReaction: addMutation.mutate,
    removeReaction: removeMutation.mutate,
    isPending: addMutation.isPending || removeMutation.isPending,
  }
}

export const useCommentReactionUsers = (socialPostId: string | null, commentId: string) => {
  const { socialApi } = useServicesContext()

  return useQuery({
    queryKey: socialKeys.commentReactions(socialPostId!, commentId),
    queryFn: () => socialApi.getCommentReactions(socialPostId!, commentId),
    enabled: !!socialPostId && !!commentId,
  })
}
